import { useRef, useState } from "react";
import { Camera, Upload, X, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * PhotoUploadButton — reusable proof-of-X uploader.
 *
 * Props:
 *  - label: button text
 *  - onUpload: async (base64) => void  — called after user confirms
 *  - disabled, testId, doneLabel
 *  - existingPhoto: if truthy, shows ✓ Done state instead of upload button
 *  - compact: smaller variant
 */
export default function PhotoUploadButton({
  label = "Upload photo",
  onUpload,
  disabled = false,
  testId = "photo-upload",
  doneLabel = "Photo uploaded",
  existingPhoto = null,
  compact = false,
}) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPEG, PNG, or WEBP images supported");
      return;
    }
    if (file.size > 4_000_000) {
      toast.error("Image too large — max 4 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!preview || !onUpload) return;
    setBusy(true);
    try {
      await onUpload(preview);
      setPreview(null);
      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setBusy(false);
    }
  };

  if (existingPhoto) {
    return (
      <div className="inline-flex items-center gap-2 text-emerald-600 text-xs font-medium" data-testid={`${testId}-done`}>
        <CheckCircle2 className="h-4 w-4" />
        {doneLabel}
      </div>
    );
  }

  if (preview) {
    return (
      <div className="inline-flex items-center gap-2" data-testid={`${testId}-preview`}>
        <img src={preview} alt="proof" className="h-10 w-10 rounded-lg border border-slate-200 object-cover" />
        <Button size="sm" onClick={submit} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`${testId}-submit`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm"}
        </Button>
        <button onClick={() => { setPreview(null); if (inputRef.current) inputRef.current.value = ""; }}
                className="p-1 text-slate-400 hover:text-slate-600" data-testid={`${testId}-cancel`}>
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <label className={`inline-flex items-center gap-1.5 ${compact ? "text-xs" : "text-sm"} font-medium text-blue-600 hover:text-blue-700 cursor-pointer ${disabled ? "opacity-50 pointer-events-none" : ""}`} data-testid={testId}>
      <Camera className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
      {label}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={onPick}
        disabled={disabled}
        data-testid={`${testId}-input`}
      />
    </label>
  );
}
